import React, { FC } from "react";
import { Helmet } from "react-helmet";
import { useFormik } from "formik";
import * as Yup from "yup";

import SectionSubscribe2 from "components/SectionSubscribe2/SectionSubscribe2";
import SocialsList from "shared/SocialsList/SocialsList";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SectionClientSay from "components/SectionClientSay/SectionClientSay";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import { contactUs } from "redux/reducers/Properties/PropertiesSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import successHandler from "utils/helpers/SuccessHandler";

export interface PageContactProps {
  className?: string;
}

const info = [
  {
    title: "💌 EMAIL",
    desc: "hello@nomadroof.com",
  },
];

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile_number: "",
      university: "",
      arrival_date: "",
      stay_duration: "",
      description: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Full name is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      mobile_number: Yup.string()
        .matches(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/, "Invalid phone number")
        .required("WhatsApp number is required"),
      university: Yup.string(),
      arrival_date: Yup.string(),
      stay_duration: Yup.string(),
      description: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = {
          contact_us: {
            name: values.name,
            email: values.email,
            mobile_number: values.mobile_number,
            university: values.university,
            arrival_date: values.arrival_date,
            stay_duration: values.stay_duration,
            description: values.description,
          },
        };
        
        await dispatch(contactUs(payload)).unwrap();

        // Success - show message and reset form
        successHandler("Thank you for contacting us! We'll get back to you soon.");
        resetForm();
      } catch (error) {
        console.error("Error submitting contact form:", error);
        alert("Failed to send message. Please try again.");
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <div
      className={`nc-PageContact overflow-hidden ${className}`}
      data-nc-id="PageContact"
    >
      <Helmet>
        <title>Contact || NomadRoof</title>
      </Helmet>
      
      {/* Hero Section with Enhanced Spacing */}
      <div className="relative">
        {/* Decorative gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F56948]/5 via-transparent to-transparent dark:from-[#F56948]/5 pointer-events-none" />
        
        <div className="relative pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
                Get in Touch
              </h2>
              <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400">
                Have questions about student housing in Lima? We'd love to hear from you.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Contact Section with Card Design */}
      <div className="relative pb-24 lg:pb-32">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="bg-white dark:bg-neutral-900 rounded-3xl shadow-2xl shadow-neutral-200/50 dark:shadow-black/50 border border-neutral-100 dark:border-neutral-800 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Column - Contact Info */}
              <div className="relative bg-[#F56948] p-12 lg:p-16">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full translate-y-24 -translate-x-24" />
                
                <div className="relative z-10 space-y-12">
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-4">
                      Let's Connect
                    </h3>
                    <p className="text-white/90 text-lg">
                      We're here to help make your exchange semester in Lima an unforgettable experience.
                    </p>
                  </div>

                  <div className="space-y-8">
                    {info.map((item, index) => (
                      <div key={index} className="group">
                        <h4 className="text-sm font-semibold text-white/80 tracking-wider mb-2 uppercase">
                          {item.title}
                        </h4>
                        <a 
                          href={`mailto:${item.desc}`}
                          className="text-white text-lg font-medium hover:text-white/80 transition-colors inline-block"
                        >
                          {item.desc}
                        </a>
                      </div>
                    ))}
                    
                    <div className="pt-4">
                      <h4 className="text-sm font-semibold text-white/80 tracking-wider mb-4 uppercase">
                        🌏 Follow Us
                      </h4>
                      <SocialsList className="mt-2 [&_a]:text-white [&_a:hover]:text-white/80 [&_a]:transition-colors" />
                    </div>
                  </div>

                  {/* Decorative quote */}
                  <div className="pt-8 border-t border-white/20">
                    <p className="text-white/80 italic text-sm">
                      "Your exchange semester starts with finding the perfect home and community."
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Contact Form */}
              <div className="p-12 lg:p-16 bg-white dark:bg-neutral-900">
                <form
                  className="space-y-6"
                  onSubmit={formik.handleSubmit}
                  noValidate
                >
                  <div className="space-y-6">
                    <div className="group">
                      <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                        Full name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Jane Doe"
                        className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948]"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.name}</div>
                      ) : null}
                    </div>

                    <div className="group">
                      <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                        Email address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="jane@example.com"
                        className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948]"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.email}</div>
                      ) : null}
                    </div>

                    <div className="group">
                      <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                        WhatsApp number *
                      </Label>
                      <Input
                        id="mobile_number"
                        name="mobile_number"
                        type="tel"
                        placeholder="+1 234 567 8900"
                        className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948]"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.mobile_number}
                      />
                      {formik.touched.mobile_number && formik.errors.mobile_number ? (
                        <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.mobile_number}</div>
                      ) : null}
                      <p className="text-neutral-500 dark:text-neutral-400 text-xs mt-1">
                        We'll reach out via WhatsApp for faster communication
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="group">
                        <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                          University
                        </Label>
                        <Input
                          id="university"
                          name="university"
                          type="text"
                          placeholder="e.g. Universidad de Lima"
                          className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948]"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.university}
                        />
                        {formik.touched.university && formik.errors.university ? (
                          <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.university}</div>
                        ) : null}
                      </div>

                      <div className="group">
                        <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                          Stay duration
                        </Label>
                        <select
                          id="stay_duration"
                          name="stay_duration"
                          className="mt-1 w-full rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948] transition-all duration-200"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.stay_duration}
                        >
                          <option value="">Select duration</option>
                          <option value="1-3 months">1-3 months</option>
                          <option value="3-6 months (semester)">3-6 months (semester)</option>
                          <option value="6-12 months (year)">6-12 months (year)</option>
                          <option value="12+ months">12+ months</option>
                        </select>
                        {formik.touched.stay_duration && formik.errors.stay_duration ? (
                          <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.stay_duration}</div>
                        ) : null}
                      </div>
                    </div>

                    <div className="group">
                      <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                        Expected arrival date
                      </Label>
                      <Input
                        id="arrival_date"
                        name="arrival_date"
                        type="date"
                        className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948]"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.arrival_date}
                      />
                      {formik.touched.arrival_date && formik.errors.arrival_date ? (
                        <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.arrival_date}</div>
                      ) : null}
                    </div>

                    <div className="group">
                      <Label className="text-neutral-800 dark:text-neutral-200 font-medium mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        rows={6}
                        placeholder="Tell us about your housing needs, budget, preferred location, etc..."
                        className="mt-1 transition-all duration-200 focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948] resize-none"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.description}
                      />
                      {formik.touched.description && formik.errors.description ? (
                        <div className="text-red-600 mt-2 text-sm font-medium">{formik.errors.description}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="pt-4">
                    <ButtonPrimary
                      type="submit"
                      disabled={formik.isSubmitting}
                      className="w-full sm:w-auto px-8 py-4 text-base font-semibold transition-all duration-200 hover:shadow-lg disabled:opacity-50"
                    >
                      {formik.isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        "Send Message"
                      )}
                    </ButtonPrimary>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Separator */}
      <div className="relative py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-100 to-transparent dark:via-neutral-800/50" />
        <div className="container max-w-7xl mx-auto px-4 relative">
          <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent" />
        </div>
      </div>

      {/* Reviews Section with Enhanced Separation */}
      <div className="relative py-24 lg:py-32 bg-neutral-50 dark:bg-neutral-900/50">
        <div className="container max-w-7xl mx-auto px-4">
          <BackgroundSection />
          <SectionClientSay uniqueClassName="Pagecontact_" />
        </div>
      </div>

      {/* Another Visual Separator */}
      <div className="relative py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-50 dark:from-neutral-900/50 to-transparent" />
        <div className="container max-w-7xl mx-auto px-4 relative">
          <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent" />
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="container max-w-7xl mx-auto px-4">
        <SectionSubscribe2 className="py-24 lg:py-32" />
      </div>
    </div>
  );
};

export default PageContact;